import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Loader from '__components/Kizen/Loader';
import { useBusinessAppPermissions } from '__pages/BusinessApps/permissions';
import { APPS_TAB_PATHS } from 'pages/Apps/constants';
import { ConfigurePanel } from '__pages/Marketplace/Details/panels/Configure';
import { UserSettingsPanel } from '__pages/Marketplace/Details/panels/UserSettings';
import { AuthorizationPanel } from '__pages/Marketplace/Details/panels/Authorization';
import { DetailsPageShell } from './DetailsPageShell';
import { usePluginAppEvents } from './usePluginAppEvents';
import { useSetTitleOnLoad } from '__hooks/useSetTitleOnLoad';
import { PLUGINS } from 'queries/query-keys';
import { KizenError } from 'services/errors';
import { getOriginalErrorStatusCode } from 'services/AxiosService';
import { PluginContext } from 'ts-components/Plugins/context';
import { useBusinessPluginApps } from 'ts-components/Plugins/useBusinessPluginApps';
import { BUSINESS_APPS_PATH, businessAppDetailsUrl } from '../../constants';
import {
  type PendingBusinessAppAction,
  useBusinessAppActions,
} from 'ts-components/Plugins/businessApps/useBusinessAppActions';
import { BUSINESS_APP_ACTIONS } from 'ts-components/Plugins/businessApps/constants';
import { useCurrentUserId } from 'hooks/useCurrentUserId';
import { ConfirmActionModal } from '../../components/ConfirmActionModal';
import { useBusinessApp } from 'ts-components/Plugins/businessApps/useBusinessApp';
import {
  liveOrParkedVersionOf,
  countMissingAppSettings,
  countMissingUserSettings,
} from 'ts-components/Plugins/businessApps/helpers';
import { AppDetailsPanel } from './AppDetailsPanel';
import { VersionHistoryPanel } from './VersionHistoryPanel';

type DetailsTab = {
  key: string;
  badge?: boolean;
  label: string;
  icon: string;
  subpath: string;
};

export const BusinessAppDetailsPage = () => {
  const { t } = useTranslation();
  const history = useHistory<{ from?: string } | undefined>();
  const { apiName } = useParams<{ apiName: string }>();
  const { search } = useLocation();
  const previewOwnerId =
    new URLSearchParams(search).get('preview_owner') ?? undefined;
  const permissions = useBusinessAppPermissions();
  const currentUserId = useCurrentUserId();
  const queryClient = useQueryClient();
  const { plugins, refreshPluginArtifacts } = useContext(PluginContext);
  const [backPath] = useState(
    () => history.location.state?.from ?? APPS_TAB_PATHS.develop
  );
  const onActionCompleted = useCallback(
    ({ action, app, version }: PendingBusinessAppAction) => {
      if (action === BUSINESS_APP_ACTIONS.view) {
        history.push(
          businessAppDetailsUrl(app.api_name, version?.preview_owner?.id)
        );
      } else if (
        action === BUSINESS_APP_ACTIONS.deletePreview ||
        action === BUSINESS_APP_ACTIONS.deleteApp ||
        (action === BUSINESS_APP_ACTIONS.deleteVersion &&
          app.versions.length === 1)
      ) {
        history.push(backPath);
      } else if (action === BUSINESS_APP_ACTIONS.publishPreview) {
        history.push(businessAppDetailsUrl(app.api_name));
      }
    },
    [history, backPath]
  );
  const { pending, selectAction, confirmPending, cancelPending } =
    useBusinessAppActions({ onCompleted: onActionCompleted });

  const {
    data: details,
    isLoading,
    isError,
    error,
  } = useBusinessApp(apiName, previewOwnerId);

  const { data: businessPluginApps, isFetched: businessPluginAppsFetched } =
    useBusinessPluginApps(true);
  const businessPluginApp = useMemo(
    () =>
      (businessPluginApps ?? []).find(
        (bpa) => bpa.plugin_app?.api_name === apiName
      ),
    [businessPluginApps, apiName]
  );
  const plugin = useMemo(
    () =>
      businessPluginApp && {
        ...businessPluginApp.plugin_app,
        name: details?.name ?? businessPluginApp.plugin_app.name,
        base_config:
          details?.base_config ??
          businessPluginApp.plugin_app.base_config ??
          {},
      },
    [businessPluginApp, details]
  );
  const settingsBusinessPluginApp = useMemo(
    () =>
      businessPluginApp &&
      plugin && { ...businessPluginApp, plugin_app: plugin },
    [businessPluginApp, plugin]
  );
  const businessPluginAppsByApiName = useMemo(
    () =>
      settingsBusinessPluginApp ? { [apiName]: settingsBusinessPluginApp } : {},
    [apiName, settingsBusinessPluginApp]
  );

  const oAuthServices = useMemo(
    () =>
      (
        (details?.is_preview
          ? details.what_this_app_adds.connections
          : businessPluginApp?.plugin_app?.services) ?? []
      ).filter((service: any) => service.auth_type === 'oauth'),
    [businessPluginApp, details]
  );

  const { oAuthEvents } = usePluginAppEvents(
    businessPluginApp,
    oAuthServices.length > 0
  );

  const refetch = useCallback(
    () =>
      Promise.all([
        refreshPluginArtifacts(apiName),
        queryClient.invalidateQueries({ queryKey: PLUGINS.BUSINESS_APPS }),
      ]),
    [apiName, queryClient, refreshPluginArtifacts]
  );

  useSetTitleOnLoad(
    t('Business Apps - {{name}}', { name: details?.name ?? apiName })
  );

  const baseConfig = useMemo<Record<string, any>>(
    () => plugin?.base_config ?? {},
    [plugin]
  );
  const hasAppSettingsIssue =
    countMissingAppSettings({
      base_config: baseConfig,
      config: businessPluginApp?.config,
    }) > 0;
  const hasUserSettingsIssue =
    countMissingUserSettings({
      base_config: baseConfig,
      employee_config: plugins[apiName]?.employee_config,
    }) > 0;
  const hasAuthIssue = (businessPluginApp?.error_count ?? 0) > 0;
  const tabs = useMemo(
    () =>
      [
        {
          key: 'details',
          label: t('App Details'),
          icon: 'info-circle',
          subpath: '',
        },
        businessPluginApp &&
          permissions.canPublishBusinessApps &&
          baseConfig.user_configurable !== false &&
          baseConfig.setup_assistant && {
            key: 'configure',
            label: t('App Settings'),
            icon: 'cog',
            badge: hasAppSettingsIssue,
            subpath: '/configure',
          },
        businessPluginApp &&
          baseConfig.user_setup_assistant && {
            key: 'user-settings',
            label: t('User Settings'),
            icon: 'user',
            badge: hasUserSettingsIssue,
            subpath: '/user-settings',
          },
        details &&
          !details.is_preview &&
          permissions.canPublishBusinessApps && {
            key: 'version',
            label: t('Version'),
            icon: 'cloud-arrow-up',
            subpath: '/version',
          },
        oAuthServices.length && {
          key: 'auth',
          label: t('Authorization'),
          icon: 'shield-check',
          badge: hasAuthIssue,
          subpath: '/auth',
        },
      ].filter(Boolean) as DetailsTab[],
    [
      t,
      businessPluginApp,
      permissions,
      baseConfig,
      details,
      oAuthServices,
      hasAppSettingsIssue,
      hasUserSettingsIssue,
      hasAuthIssue,
    ]
  );

  const basePath = `${BUSINESS_APPS_PATH}/apps/${apiName}`;

  if (isError) {
    const status = getOriginalErrorStatusCode(error);
    if (status === 404 && previewOwnerId) {
      return <Redirect to={basePath} />;
    }
    throw new KizenError(status ?? 404);
  }

  if (isLoading || !details) {
    return <Loader loading={isLoading} />;
  }

  const version = details.is_preview
    ? details.versions.find(
        (entry) => entry.preview_owner?.id === details.preview_owner?.id
      )
    : liveOrParkedVersionOf(details);
  const panels: Record<string, JSX.Element | null> = {
    details: (
      <AppDetailsPanel
        details={details}
        version={version}
        permissions={permissions}
        currentUserId={currentUserId}
        onSelectAction={selectAction}
      />
    ),
    configure:
      plugin && settingsBusinessPluginApp ? (
        <ConfigurePanel
          plugin={plugin}
          businessPluginAppsByApiName={businessPluginAppsByApiName}
          refetch={refetch}
          businessPluginApp={settingsBusinessPluginApp}
          canEditJSON={permissions.canPublishBusinessApps}
          canImportSchema={!details.is_preview}
        />
      ) : null,
    'user-settings':
      plugin && settingsBusinessPluginApp ? (
        <UserSettingsPanel
          plugin={plugin}
          businessPluginAppsByApiName={businessPluginAppsByApiName}
          refetch={refetch}
          businessPluginApp={settingsBusinessPluginApp}
          canEditJSON={permissions.canPublishBusinessApps}
        />
      ) : null,
    version: (
      <VersionHistoryPanel
        details={details}
        permissions={permissions}
        onSelectAction={selectAction}
      />
    ),
    auth: (
      <AuthorizationPanel
        oAuthServices={oAuthServices}
        businessPluginApp={businessPluginApp ?? {}}
        oAuthEvents={oAuthEvents}
        refetch={refetch}
      />
    ),
  };

  return (
    <DetailsPageShell
      title={details.name}
      backTo={backPath}
      backLabel={t('back')}
      currentSearch={search}
      tabs={tabs.map((tab) => ({
        label: tab.label,
        path: `${basePath}${tab.subpath}`,
        icon: tab.icon,
        badge: tab.badge,
        content: panels[tab.key],
      }))}
      fallback={
        businessPluginAppsFetched ? (
          <Redirect to={{ pathname: basePath, search }} />
        ) : null
      }
    >
      {pending ? (
        <ConfirmActionModal
          pending={pending}
          onConfirm={confirmPending}
          onHide={cancelPending}
        />
      ) : null}
    </DetailsPageShell>
  );
};
